import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ArchivedTable from 'components/Campaigns/Tables/ArchivedTable';
import TableTabs from 'components/TableTabs';
import { SearchInput, Widget } from '@spins/react-ui-library';
import CampaignsTable from 'components/Campaigns/Tables/CampaignsTable';
import { Status, ListCampaignsDocument as query } from 'generated/campaigns-graphql';
import { WatchQueryFetchPolicy } from 'apollo-client';
import { campaignsClient } from 'apollo';
import { getData } from 'utils/functions';
import {
  CAMPAIGNS_TYPE,
  CAMPAIGNS_ARCHIVED_TYPE,
  CAMPAIGNS_ACTIVE_TYPE,
  CAMPAIGNS_INACTIVE_TYPE,
} from 'utils/types';
import {
  clientIdSelector,
  currentBrandSelector,
  isEnterpriseContextSelector,
} from 'store/global/selectors';
import * as selectors from 'containers/Campaigns/selectors';
import * as createEditActions from 'containers/Campaigns/CreateEdit/store/actions';
import * as actions from 'containers/Campaigns/actions';
import { Alert, Button } from 'react-bootstrap';
import 'containers/Campaigns/TablesWidget/styles.scss';
import {
  CampaignColumnsKeysType,
  CampaignUpdateActionContext,
  refactorCampaigns,
} from 'utils/campaigns';
import ColumnsSelector from 'components/Campaigns/ColumnsSelector';
import {
  useTableQueryState,
  useTableQuery,
  QueryResult,
  QueryStateResult,
} from '@spins/react-table';
import {
  ArchiveCampaignDocument as archiveCampaignQuery,
  ArchiveCampaignMutation as archiveCampaignResult,
  ArchiveCampaignMutationVariables as archiveCampaignVars,
} from 'generated/campaigns-graphql';
import { UNABLE_CLICK_TO } from 'utils/strings';
import { useCampaignsState } from 'providers/campaigns';
import { useMutation } from '@apollo/client';

interface Props {
  onRowClick(key: string, elem: any): void;
}

const CampaignsTablesWidget: React.FC<Props> = ({ onRowClick }) => {
  const { refetchFunctions, setRefetchFunctions } = useCampaignsState();

  const [isRefetchLoading, setRefetchLoading] = useState({
    [CAMPAIGNS_TYPE]: false,
    [CAMPAIGNS_ARCHIVED_TYPE]: false,
  });

  const dispatch = useDispatch();

  //selectors
  const archivedColumns = useSelector<Array<CampaignColumnsKeysType>>(
    selectors.archivedColumnsSelector
  );
  const campaignsColumns = useSelector<Array<CampaignColumnsKeysType>>(
    selectors.campaignsColumnsSelector
  );
  const clientId = useSelector<string>(clientIdSelector);
  const currentBrand = useSelector<string>(currentBrandSelector);
  const currentTab = useSelector<string>(selectors.currentTabSelector);
  const isEnterpriseContext = useSelector<boolean>(isEnterpriseContextSelector);
  const selectedCampaign = useSelector<string>(selectors.selectedCampaignSelector);

  const tabsConfig = {
    [CAMPAIGNS_TYPE]: {
      status: [Status[CAMPAIGNS_ACTIVE_TYPE], Status[CAMPAIGNS_INACTIVE_TYPE]],
      editModal: CampaignUpdateActionContext.CAMPAIGNS,
      columns: campaignsColumns,
      selectColumn: (id) => actions.selectCampaignColumn(id, campaignsColumns),
    },
    [CAMPAIGNS_ARCHIVED_TYPE]: {
      status: [Status[CAMPAIGNS_ARCHIVED_TYPE]],
      editModal: CampaignUpdateActionContext.ARCHIVED,
      columns: archivedColumns,
      selectColumn: (id) => actions.selectArchivedColumn(id, archivedColumns),
    },
  };

  // tables state
  const tablesState: QueryStateResult = useTableQueryState([
    {
      id: 'campaigns',
      defaultSortAttribute: 'startDate',
      queryVariables: {
        clientID: clientId,
        ...(isEnterpriseContext && { brand: currentBrand }),
        status: tabsConfig[CAMPAIGNS_TYPE].status,
      },
    },
    {
      id: 'archivedCampaigns',
      defaultSortAttribute: 'startDate',
      queryVariables: {
        clientID: clientId,
        ...(isEnterpriseContext && { brand: currentBrand }),
        status: tabsConfig[CAMPAIGNS_ARCHIVED_TYPE].status,
      },
    },
  ]);

  // campaigns query
  const {
    data: campaignsData,
    loading: campaignsLoading,
    refetch: campaignsRefetch,
    error: campaignsError,
  }: QueryResult = useTableQuery(query, {
    client: campaignsClient,
    fetchPolicy: 'cache-first' as WatchQueryFetchPolicy,
    queryState: tablesState.queryState('campaigns'),
    searchAttributes: ['name'],
  });

  // archived campaigns query
  const {
    data: archivedData,
    loading: archivedLoading,
    refetch: archivedRefetch,
    error: archivedError,
  }: QueryResult = useTableQuery(query, {
    client: campaignsClient,
    fetchPolicy: 'cache-first' as WatchQueryFetchPolicy,
    queryState: tablesState.queryState('archivedCampaigns'),
    searchAttributes: ['name'],
  });

  const refetchData = (tabKey: string) => {
    setRefetchLoading((prevState) => ({ ...prevState, [tabKey]: true }));
    const refetch = tabKey === CAMPAIGNS_TYPE ? campaignsRefetch : archivedRefetch;
    refetch()
      .catch((err) => onError(err, tabKey))
      .finally(() => setRefetchLoading((prevState) => ({ ...prevState, [tabKey]: false })));
  };

  const [archiveCampaign] = useMutation<archiveCampaignResult, archiveCampaignVars>(
    archiveCampaignQuery,
    {
      client: campaignsClient as any,
      onCompleted: () => {
        refetchFunctions.listCampaigns(CAMPAIGNS_TYPE);
        refetchFunctions.listCampaigns(CAMPAIGNS_ARCHIVED_TYPE);
      },
      onError: () => {
        dispatch(
          actions.setErrorMessage(
            `An unexpected error occurred while trying to archive campaign. Please retry later.`
          )
        );
      },
    }
  );

  const handleAction = (key: string, id: string) => {
    switch (key) {
      case 'Edit':
        dispatch(createEditActions.openEditCampaignModal(id, tabsConfig[currentTab].editModal));
        break;
      case 'View':
        dispatch(actions.openViewCampaignModal(id));
        break;
      case 'Archive':
        archiveCampaign({
          variables: {
            id,
          },
        });
        break;
    }
  };

  const handleCampaignColumnSelect = (id: CampaignColumnsKeysType) => {
    dispatch(tabsConfig[currentTab].selectColumn(id));
  };

  const handleTabSelect = (value: string) => {
    dispatch(actions.selectTab(value));
  };

  const renderErrorAlert = (tabKey: string) => {
    return (
      <Alert variant="danger" style={{ textAlign: 'center' }}>
        {UNABLE_CLICK_TO}
        <Button
          variant="link"
          onClick={() => refetchData(tabKey)}
          style={{ height: 'auto', display: 'contents' }}
        >
          retry.
        </Button>
      </Alert>
    );
  };

  const onError = (error: any, tabKey: string) => {
    return renderErrorAlert(tabKey);
  };

  useEffect(() => {
    if (!!campaignsData) {
      const refactoredCampaigns = refactorCampaigns(getData(campaignsData).data);
      refactoredCampaigns.length > 0 && dispatch(actions.selectCampaign(refactoredCampaigns[0].id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignsData]);

  useEffect(() => {
    setRefetchFunctions({
      ...refetchFunctions,
      listCampaigns: (key: string) => refetchData(key),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignsRefetch, archivedRefetch]);

  const campaignsList = campaignsData?.list?.data;
  const campaignsTotalItems = campaignsData?.list?.page?.totalItems;
  const campaignsRows =
    campaignsList && campaignsList.length > 0 ? refactorCampaigns(campaignsList) : [];
  const totalCampaignsRows =
    campaignsList && campaignsList.length > 0 && campaignsTotalItems ? campaignsTotalItems : 0;

  const archivedList = archivedData?.list?.data;
  const archivedTotalItems = archivedData?.list?.page?.totalItems;
  const archivedRows =
    archivedList && archivedList.length > 0 ? refactorCampaigns(archivedList) : [];
  const totalArchivedRows =
    archivedList && archivedList.length > 0 && archivedTotalItems ? archivedTotalItems : 0;

  return (
    <Widget>
      {{
        header: {
          content: {
            left: (
              <TableTabs
                selectedTab={currentTab}
                tabs={[
                  {
                    name: 'Campaigns',
                    total: !!campaignsData ? campaignsTotalItems : 0,
                  },
                  {
                    name: 'Archived',
                    total: !!archivedData ? archivedTotalItems : 0,
                  },
                ]}
                onSelect={handleTabSelect}
              />
            ),
            center: (
              <SearchInput
                className="search-field"
                placeholder="Search..."
                onChange={(value) => {
                  tablesState.sharedState.searching.search(value);
                }}
              />
            ),
            right: (
              <ColumnsSelector
                selected={tabsConfig[currentTab].columns}
                onSelect={handleCampaignColumnSelect}
              />
            ),
          },
        },
        body: {
          style: { padding: '0px 15px' },
          content: (
            <>
              <div>
                <div
                  key="Campaigns"
                  style={{ display: currentTab === 'Campaigns' ? 'block' : 'none' }}
                >
                  {campaignsError && !isRefetchLoading ? (
                    onError(campaignsError, CAMPAIGNS_TYPE)
                  ) : (
                    <CampaignsTable
                      key={campaignsColumns} // key present to re-render after columns change
                      queryState={tablesState.queryState('campaigns')}
                      showLoading={campaignsLoading || isRefetchLoading[CAMPAIGNS_TYPE]}
                      rows={campaignsRows}
                      totalRows={totalCampaignsRows}
                      selectedCampaign={selectedCampaign}
                      onActionClick={handleAction}
                      onRowClick={(row) => onRowClick(row.name, row)}
                      onCampaignSelect={(id) => dispatch(actions.selectCampaign(id))}
                      columnsShown={campaignsColumns}
                    />
                  )}
                </div>
                <div
                  key="Archived"
                  style={{ display: currentTab === 'Archived' ? 'block' : 'none' }}
                >
                  {archivedError && !isRefetchLoading ? (
                    onError(archivedError, CAMPAIGNS_ARCHIVED_TYPE)
                  ) : (
                    <ArchivedTable
                      key={archivedColumns} // key present to re-render after columns change
                      queryState={tablesState.queryState('archivedCampaigns')}
                      showLoading={archivedLoading || isRefetchLoading[CAMPAIGNS_TYPE]}
                      rows={archivedRows}
                      totalRows={totalArchivedRows}
                      onActionClick={handleAction}
                      onRowClick={(row) => onRowClick(row.name, row)}
                      columnsShown={archivedColumns}
                    />
                  )}
                </div>
              </div>
            </>
          ),
        },
      }}
    </Widget>
  );
};

export default CampaignsTablesWidget;
